import { Base64 } from 'js-base64';
import { z } from 'zod';

/**
 * v3 selection context. Unlike v1/v2 — which carried structured
 * `ExtractedParagraph[]` and were re-serialized server-side into the
 * pipe-delimited format — v3 carries the **already-serialized HTML-like Cast
 * markup** produced client-side by `serializeSelectionContext`. Expansion and
 * serialization happen on the editor side, so the payload is the compact
 * selection neighborhood (with `<selection-start/>` / `<selection-end/>`
 * markers), not the whole document.
 *
 * `focusSuggestionId` carries the identity of the suggestion marker the user is
 * refining (Refine AI Edit), mirroring v2; `null` when absent.
 */
export type SelectionContextV3 = {
  documentId: string;
  focusSuggestionId: string | null;
  html: string;
};

export const SelectionContextV3Schema = z.object({
  documentId: z.string(),
  focusSuggestionId: z.string().nullable(),
  html: z.string(),
});

/**
 * Encodes v3 selection context to base64url for storage in a mention element.
 * Same scheme as v1/v2 — only the data shape (pre-serialized HTML) differs.
 */
export function encodeSelectionContextV3(context: SelectionContextV3): string {
  return Base64.encodeURL(JSON.stringify(context));
}

/**
 * Decodes base64url-encoded v3 selection context. Returns null on decode
 * failure or when the payload doesn't carry the v3 shape.
 */
export function decodeSelectionContextV3(encoded: string): SelectionContextV3 | null {
  try {
    const json = Base64.decode(encoded);
    const parsed: unknown = JSON.parse(json);
    const result = SelectionContextV3Schema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
}
